import { DrawingSingleton, type DrawingModuleType } from "./DrawingSingleton";
import type { DrawingModule } from "./types/DrawingWeb";
import { deleteObj } from "./core/delete";
import { OpenMode } from "./core/constants";
import type { ICommand } from "./commands/ICommand";
import {
  type DrawingNoteConfig,
  type DrawingLayerConfig,
  DEFAULT_NOTE_CONFIG,
} from "./types/NoteConfig";
import { LoadingTimer } from "@/utils/LoadingTimer";
import { clearBlockCache } from "./blocks";
import { Logger } from "@/logger";
import {
  captureUpdateTelemetry,
  noteRegenAll,
} from "./telemetry/updateTelemetry";
import type { ViewChangedPayload } from "./interaction/types";
import { isDarkBackground } from "./overlay/OverlayTheme";

// Forward declaration to avoid circular dependency
type CommandExecutor = (command: ICommand) => void;
type CommandUndo = () => boolean;
type CommandRedo = () => boolean;

/**
 * Main viewer class for DrawingJS.
 * Wraps Module.App and handles rendering, navigation, and file operations.
 */
export class DRDrawingViewer {
  readonly module: DrawingModuleType;
  readonly canvas: HTMLCanvasElement;
  readonly app: DrawingModule.App;

  services: DrawingModule.Services;
  database: DrawingModule.OdDbDatabase | null = null;
  /**
   * Database left over from a previous closeFile(). App holds only a raw
   * (non-owning) pointer to the current database — see closeFile() — so this
   * can't be deleted until App has been repointed at a new one. Released by
   * releasePreviousDatabase(), called right after the next createNew()/openFile()
   * calls app.setDb()/App.OpenFile() — or immediately in dispose(), when this
   * viewer instance is being discarded and no "next" database is coming.
   */
  private _pendingDatabaseRelease: DrawingModule.OdDbDatabase | null = null;
  private static _databasesReleasedCount = 0;
  private animationFrameId: number | null = null;
  private backgroundColor = 0xffffff;
  static readonly PAPER_SPACE_BG_COLOR = 0xa0a0a0;
  private filePath = "";
  private isRegenNeeded = false;

  /** Callback for executing commands (set by Facade) */
  commandExecutor: CommandExecutor | null = null;

  /** Callback for undoing commands (set by Facade) */
  commandUndo: CommandUndo | null = null;

  /** Callback for redoing commands (set by Facade) */
  commandRedo: CommandRedo | null = null;

  /** Overlay manager for preview graphics (set by Facade) */
  overlayManager: import("./overlay").OverlayManager | null = null;

  /** Active note configuration for new entities */
  activeNoteConfig: DrawingNoteConfig = DEFAULT_NOTE_CONFIG;

  /** Layer configurations for the current legend */
  activeLayerConfigs: DrawingLayerConfig[] = [];

  /** Smoothening epsilon value (0 = disabled) */
  smoothen: number = 0;

  /** Auto-close freehand polylines when endpoints are close (default: true) */
  autoClose: boolean = true;

  /** Add a text label at the center of shapes when drawing (default: true) */
  labelEnabled: boolean = true;

  /** Auto-close threshold in screen pixels (default: 20) */
  autoCloseThreshold: number = 20;

  /** View-changed subscribers — see onViewChanged(). Populated by Facade, which
   *  forwards InteractionManager's navigationHandler.onViewChanged() here so
   *  draggers can reproject overlays after pan/zoom without depending on Facade. */
  private viewChangedHandlers: Array<(payload: ViewChangedPayload) => void> =
    [];

  constructor(canvas: HTMLCanvasElement, module: DrawingModuleType) {
    this.canvas = canvas;
    this.module = module;
    this.module.canvas = canvas;

    // Reuse page-lifetime singletons from DrawingSingleton — see comment there for rationale.
    this.services = DrawingSingleton.getOrCreateServices();
    this.app = DrawingSingleton.getOrCreateApp();
  }

  /**
   * Initialize the viewer. Must be called before any other operations.
   */
  async initialize(): Promise<void> {
    // Future initialization logic
  }

  /**
   * Execute a command through the registered executor.
   */
  executeCommand(command: ICommand): void {
    if (this.commandExecutor) {
      this.commandExecutor(command);
    } else {
      // Fallback: just execute without history
      command.execute();
    }
  }

  /**
   * Subscribe to view change events (pan, zoom, pinch, resize, extents).
   * Lets draggers reproject overlays after navigation without depending on
   * Facade/InteractionManager directly — see viewChangedHandlers above.
   * @returns Unsubscribe function
   */
  onViewChanged(handler: (payload: ViewChangedPayload) => void): () => void {
    this.viewChangedHandlers.push(handler);
    return () => {
      const index = this.viewChangedHandlers.indexOf(handler);
      if (index >= 0) {
        this.viewChangedHandlers.splice(index, 1);
      }
    };
  }

  /** Emit a view-changed event to all subscribers. Called by Facade. */
  emitViewChanged(payload: ViewChangedPayload): void {
    for (const handler of this.viewChangedHandlers) {
      handler(payload);
    }
  }

  /**
   * Undo the last command through the registered callback.
   * @returns true if undo was performed
   */
  undoCommand(): boolean {
    if (this.commandUndo) {
      return this.commandUndo();
    }
    return false;
  }

  /**
   * Redo the last undone command through the registered callback.
   * @returns true if redo was performed
   */
  redoCommand(): boolean {
    if (this.commandRedo) {
      return this.commandRedo();
    }
    return false;
  }

  /**
   * Open a DWG file from ArrayBuffer.
   */
  async openFile(filename: string, arrayBuffer: ArrayBuffer): Promise<void> {
    if (this.database) {
      Logger.captureException(
        new Error(
          "openFile() called while a database was already open (closeFile() was not called first)",
        ),
        { filePath: this.filePath },
      );
    }

    clearBlockCache();
    LoadingTimer.step("safeCreateFile");
    this.filePath = DrawingSingleton.safeCreateFile(filename, arrayBuffer);

    LoadingTimer.step("app.OpenFile");
    this.app.OpenFile(this.filePath);

    this.database = this.app.getDb();
    // Safe now: App.OpenFile() has already repointed App at the new database above.
    this.releasePreviousDatabase();

    // Enable multithreading for better performance on large drawings
    // LoadingTimer.step("enableMultithreading");
    // this.enableMultithreading();

    // Enable GPU selection by default
    LoadingTimer.step("setGpuSelection");
    this.setGpuSelection(true);

    // Configure device caches for performance
    LoadingTimer.step("configureDeviceCache");
    this.configureDeviceCache();

    // Set 2D optimized render mode for best performance
    LoadingTimer.step("setRenderMode2DOptimized");
    this.setRenderMode2DOptimized();

    // After OpenFile (the device exists), before the first render.
    LoadingTimer.step("setBackgroundColor");
    this._setBackgroundColor(this.getEffectiveBackgroundColor());
  }

  /**
   * Configure device caches for better performance.
   * Enables block caching, TTF font caching, and double buffering.
   */
  private configureDeviceCache(): void {
    const device = this.app.getDevice();
    if (!device) return;

    const properties = device.properties();

    // Enable block structure caching
    this.setDevicePropertyBool(properties, "BlocksCache", true);

    // Enable TrueType font caching
    this.setDevicePropertyBool(properties, "UseTTFCache", true);

    // Enable double buffering to avoid flickering
    this.setDevicePropertyBool(properties, "DoubleBufferEnabled", true);
  }

  /**
   * Set render mode to 2D optimized for best performance on 2D drawings.
   * k2DOptimized (0) is the fastest mode for 2D content.
   */
  private setRenderMode2DOptimized(): void {
    const device = this.app.getDevice();
    if (!device) return;

    const view = device.viewAt(0);
    if (!view) return;

    // RenderMode.k2DOptimized = 0
    // This is the most performant mode for 2D drawings
    const k2DOptimized = 0;
    view.setMode(k2DOptimized);
  }

  /**
   * Set a boolean property on the device.
   */
  private setDevicePropertyBool(
    properties: DrawingModule.OdRxDictionary,
    name: string,
    value: boolean,
  ): void {
    const propName = new this.module.OdString(name);
    try {
      if (properties.has(propName)) {
        const propPointer = properties.getAt(propName);
        if (propPointer) {
          const odRxVariant = this.module.OdRxVariant.cast(propPointer.get());
          const odRxVariantValue =
            this.module.OdRxVariantValue.cast(odRxVariant);
          const odVariant = odRxVariantValue.asOdVariant();
          odVariant.setBool(value);
        }
      }
    } catch {
      // Property may not exist in this build
    } finally {
      deleteObj(propName);
    }
  }

  /**
   * Enable or disable GPU-accelerated selection.
   */
  setGpuSelection(enabled: boolean): void {
    const device = this.app.getDevice();
    if (!device) return;

    const properties = device.properties();
    const propName = new this.module.OdString("GPUSelectionEnable");
    const propertyPointer = properties.getAt(propName);
    deleteObj(propName);

    if (propertyPointer) {
      // Get the variant value: OdRxObjectPtr -> OdRxVariant -> OdRxVariantValue -> OdVariant
      // Taken from https://cloud.opendesign.com/examples/#/sandbox/drawinginweb/01_Basic/07_OpenAndSelect
      const odRxVariant = this.module.OdRxVariant.cast(propertyPointer.get());
      const odRxVariantValue = this.module.OdRxVariantValue.cast(odRxVariant);
      const odVariant = odRxVariantValue.asOdVariant();

      // Set the boolean value - modifying in place, no need to putAt
      odVariant.setBool(enabled);
    }
  }

  /**
   * Allows to control if scene graph is enable (true) or not (false)
   * @returns boolean (UseSceneGraph value or false anyway)
   */
  getUseSceneGraph(): boolean {
    const device = this.app.getDevice();
    const properties = device.properties();
    const key = new this.module.OdString("UseSceneGraph");
    let result = false;
    if (properties.has(key)) {
      const propertyPointer = properties.getAt(key);
      if (propertyPointer) {
        const odRxVariant = this.module.OdRxVariant.cast(propertyPointer.get());
        const odRxVariantValue = this.module.OdRxVariantValue.cast(odRxVariant);
        result = odRxVariantValue.asOdVariant().getBool();
      }
    }
    deleteObj(key);
    return result;
  }

  /**
   * allows to toggle the use of scene graph
   * @param value boolean value to set useScreneGraph
   */
  setUseSceneGraph(value: boolean) {
    const device = this.app.getDevice();
    const properties = device.properties();
    const key = new this.module.OdString("UseSceneGraph");
    if (properties.has(key)) {
      const propertyPointer = properties.getAt(key);
      this.module.OdRxVariantValue.cast(
        this.module.OdRxVariant.cast(propertyPointer.get()),
      )
        .asOdVariant()
        .setBool(value);
      properties.putAt(
        key,
        propertyPointer.get(),
        new this.module.ODCOLORREF(undefined),
      );
    }
    deleteObj(key);
  }

  /**
   * Return the name of the currently active layout ("Model" or a paper space name).
   */
  getActiveLayout(): string {
    if (!this.database) return "Model";
    return this.database.findActiveLayout(true).toString();
  }

  /**
   * Recreate the render device after a layout switch and reapply all device settings.
   * Called by LayoutManager — layout DB operations happen there, this handles the render side.
   */
  applyLayoutSwitch(bgColor: number): void {
    // Do NOT release the outgoing device here — App owns it, see createDevice()
    // note in createNew() below.
    this.app.createDevice();
    this.app.setDb(this.database!);
    this.setGpuSelection(true);
    this.configureDeviceCache();
    this.setRenderMode2DOptimized();
    this._setBackgroundColor(bgColor);
    this.resize();
    this.update();
  }

  /**
   * Create a new empty drawing.
   */
  createNew(): void {
    if (this.database) {
      Logger.captureException(
        new Error(
          "createNew() called while a database was already open (closeFile() was not called first)",
        ),
      );
    }

    this.database = this.services.createDatabase();
    // Unlike the database, the device must NOT be released on the JS side.
    // getDb() returns a raw, non-owning pointer (hence releasePreviousDatabase()),
    // but App holds its device via a smart pointer (OdGsDevicePtr): createDevice()
    // releases the previous one itself. Any deleteObj() on the outgoing device is
    // therefore a double free, which ODA reports as a WASM "table index is out of
    // bounds" trap (destructor called on already-freed memory) — observed in both
    // possible orderings:
    //   - deleteObj BEFORE createDevice() → App dereferences an already-freed device;
    //   - deleteObj AFTER createDevice() → createDevice() already freed it (crash
    //     reproduced when switching to paper space via PaperSpaceMenu).
    // Consistent with the rest of the code: the ~25 app.getDevice() calls across the
    // project never delete their result. Nothing leaks on the C++ side; only the
    // embind wrapper (a few bytes of JS heap) is left behind, which is negligible.
    this.app.createDevice();
    this.app.setDb(this.database);
    // Safe now: App was just repointed at the new database above.
    this.releasePreviousDatabase();

    this._setBackgroundColor(this.backgroundColor);

    // Enable GPU selection by default
    this.setGpuSelection(true);

    // Configure device caches for performance
    this.configureDeviceCache();

    // Set 2D optimized render mode for best performance
    this.setRenderMode2DOptimized();

    this.resize();
    this.update();
  }

  /**
   * Check that the loaded DrawingWeb.js can actually write files.
   * The Protection Server serves a disabled version when the URL check fails:
   * writeFile1 exists but produces an empty buffer.
   * Call this right after openFile() to detect the issue before the user edits anything.
   */
  async checkWriteCapability(): Promise<boolean> {
    const { OdString } = this.module;
    const FS = (window as any).FS;
    const pDb = this.app.getDb();
    const testFilename = "_write_capability_test.dwg";
    const fileOut = new OdString(testFilename);
    try {
      pDb.writeFile1(fileOut, 0, 33, false, 16);
      deleteObj(fileOut);
      const buf = FS.readFile(testFilename) as Uint8Array;
      return !!buf && buf.length > 0;
    } catch {
      return false;
    } finally {
      try {
        FS.unlink(testFilename);
      } catch {
        // ignore — file may not exist if writeFile1 failed
      }
    }
  }

  /**
   * Save the current drawing to a DWG file.
   */

  saveFile(_filename: string): Blob {
    const { OdString } = this.module;
    const pDb = this.app.getDb();
    const device = this.app.getDevice();
    const view = device.viewAt(0);
    const currentTarget = view.target();
    const currentPosition = view.position();
    const currentFieldHeight = view.fieldHeight();
    const activeViewportId = pDb
      .activeViewportId()
      .safeOpenObject(OpenMode.kForWrite, false);
    const viewport = this.module.OdDbViewportTableRecord.cast(activeViewportId);
    if (viewport) {
      // Set viewport center (viewport position on screen) - should be OdGePoint2d
      const centerPoint = new this.module.OdGePoint2d(0, 0);
      viewport.setCenterPoint(centerPoint);
      deleteObj(centerPoint);
      // Set view target (where the camera looks)
      viewport.setTarget(currentTarget);
      // Set view direction (camera vector)
      const viewDirection = new this.module.OdGeVector3d(
        currentPosition.x - currentTarget.x,
        currentPosition.y - currentTarget.y,
        currentPosition.z - currentTarget.z,
      );
      viewport.setViewDirection(viewDirection);
      deleteObj(viewDirection);
      // Set viewport height and width
      // Use fieldHeight as viewport height
      viewport.setHeight(currentFieldHeight);
      viewport.setWidth(currentFieldHeight); // proportionally
      // Set elevation (camera height)
      viewport.setElevation(currentPosition.z);
    } else {
      console.error("Failed to cast to OdDbViewportTableRecord");
    }
    deleteObj(currentTarget);
    deleteObj(currentPosition);
    deleteObj(activeViewportId);

    const FS = (window as any).FS;

    // Use simple filename like in ODA's 09_SaveDwg example
    // Writing to root of virtual filesystem (no path prefix)
    const outputFilename = "drawing_result.dwg";

    const fileOut = new OdString(outputFilename);

    pDb.writeFile1(
      fileOut,
      0, // SaveType.kDwg
      33, // DwgVersion.kDHL_CURRENT
      false,
      16,
    );
    deleteObj(fileOut);

    const buf = FS.readFile(outputFilename) as Uint8Array;
    if (!buf || buf.length === 0) {
      throw new Error("ODA writeFile1 produced an empty file — save aborted");
    }
    const blob = new Blob([new Uint8Array(buf)]);
    // Clean up the temp file from virtual filesystem
    try {
      FS.unlink(outputFilename);
    } catch {
      // Ignore if file doesn't exist or can't be deleted
    }

    return blob;
  }

  /**
   * Check if a file is open.
   */
  isFileOpen(): boolean {
    return this.database !== null;
  }

  /**
   * Open a DWG from an ArrayBuffer as a standalone OdDbDatabase.
   * The returned database is independent of the active drawing.
   * Caller is responsible for calling deleteObj() on the returned database when done.
   */
  openDatabaseFromBuffer(
    name: string,
    arrayBuffer: ArrayBuffer,
  ): DrawingModule.OdDbDatabase {
    const filePath = DrawingSingleton.safeCreateFile(name, arrayBuffer);
    const db = this.services.readFile(filePath);
    try {
      (window as any).FS.unlink(filePath);
    } catch {
      // ignore — file may not exist
    }
    return db;
  }

  // ==================== Coordinate Conversion ====================

  /**
   * Convert screen coordinates to world coordinates.
   * Uses OdGsView.worldToDeviceMatrix().inverse()
   * Be careful z is infered from GsMatrix and not a projection on drawing.
   */
  screenToWorld(screen: { x: number; y: number }): {
    x: number;
    y: number;
    z: number;
  } {
    // Get active view from device (references - do NOT delete)
    const gsDevice = this.app.getDevice();
    const gsView = gsDevice.viewAt(0);

    // Get world-to-device matrix and invert to get device-to-world
    // These are new objects that we must delete
    const w2dMatrix = gsView.worldToDeviceMatrix();
    let d2wMatrix: ReturnType<typeof w2dMatrix.inverse>;
    try {
      d2wMatrix = w2dMatrix.inverse();
    } catch (e) {
      deleteObj(w2dMatrix);
      Logger.captureException(
        new Error(
          "screenToWorld: OdGeMatrix3d.inverse() crashed (WASM memory access out of bounds)",
        ),
        {
          screen_x: screen.x,
          screen_y: screen.y,
          hasDevice: !!gsDevice,
          hasView: !!gsView,
          viewIsValid: gsView.isValid(),
          deviceIsValid: gsDevice.isValid(),
          hasMatrix: !!w2dMatrix,
          originalError: String(e),
        },
      );
      throw e;
    }

    // Create device point (screen coords with z=0)
    const devicePoint = new this.module.OdGePoint3d(screen.x, screen.y, 0);

    // Transform to world coordinates
    devicePoint.transformBy(d2wMatrix);

    const result = {
      x: devicePoint.x,
      y: devicePoint.y,
      z: 0, // worldToDeviceMatrix inverse produces z≈1e+20 (far-plane artifact) — force 0
    };

    // Cleanup only the objects WE created (not internal references)
    deleteObj(devicePoint);
    deleteObj(d2wMatrix);
    deleteObj(w2dMatrix);
    // Do NOT delete gsView and gsDevice - they are owned by the App

    return result;
  }

  /**
   * Convert world coordinates to screen coordinates.
   * Uses OdGsView.worldToDeviceMatrix()
   */
  worldToScreen(world: { x: number; y: number; z?: number }): {
    x: number;
    y: number;
  } {
    // Get active view from device (references - do NOT delete)
    const gsDevice = this.app.getDevice();
    const gsView = gsDevice.viewAt(0);

    // Get world-to-device matrix
    const w2dMatrix = gsView.worldToDeviceMatrix();

    // Create world point
    const worldPoint = new this.module.OdGePoint3d(
      world.x,
      world.y,
      world.z ?? 0,
    );

    // Transform to device (screen) coordinates
    worldPoint.transformBy(w2dMatrix);

    const result = {
      x: worldPoint.x,
      y: worldPoint.y,
    };

    // Cleanup
    deleteObj(worldPoint);
    deleteObj(w2dMatrix);

    return result;
  }

  /**
   * Convert a distance in screen pixels to world units.
   */
  screenDistanceToWorld(screenDistance: number): number {
    const origin = this.screenToWorld({ x: 0, y: 0 });
    const offset = this.screenToWorld({ x: screenDistance, y: 0 });
    return Math.abs(offset.x - origin.x);
  }

  /**
   * Convert a distance in world units to screen pixels.
   */
  worldDistanceToScreen(worldDistance: number): number {
    const origin = this.worldToScreen({ x: 0, y: 0 });
    const offset = this.worldToScreen({ x: worldDistance, y: 0 });
    return Math.abs(offset.x - origin.x);
  }

  // ==================== Background ====================

  /**
   * set the value of background property
   */

  setBackgroundColor(color: number): void {
    this.backgroundColor = color;
  }

  /**
   * Change the plan background while the drawing is open, on the existing
   * device. Never reopen the file for this: App.OpenFile reloaded the opening
   * state behind the tools' back (they kept editing `this.database`) and the
   * next save, which serializes app.getDb(), lost the session's edits.
   *
   * The palette is resolved when geometry is vectorized, so a full regen
   * redraws palette colors (ACI 7 white ↔ black) with the new one. In a
   * paper-space layout only the color is stored: the sheet keeps its light
   * background and applyLayoutSwitch() applies it back in Model.
   */
  applyBackgroundColorLive(color: number): void {
    this.backgroundColor = color;
    if (!this.database || this.getActiveLayout() !== "Model") return;
    this._setBackgroundColor(color);
    this.app.regenAll();
    noteRegenAll("setBackgroundColor");
    this.app.getDevice()?.invalidate();
    this.update("setBackgroundColor");
  }

  /**
   * Background color actually applied to the device.
   *
   * Paper space always renders on a light background (the sheet itself is white),
   * so the user's dark background setting must not be applied there — otherwise
   * the drawing is rendered with the dark palette (light lines) on white paper.
   */
  getEffectiveBackgroundColor(): number {
    return this.getActiveLayout() !== "Model"
      ? DRDrawingViewer.PAPER_SPACE_BG_COLOR
      : this.backgroundColor;
  }

  /**
   *
   * @param color
   */
  _setBackgroundColor(color: number): void {
    const device = this.app.getDevice();
    if (device) {
      device.setBackgroundColor(color);
      // Update palette to match background for proper contrast
      // This makes index 7 (white) appear correctly on both backgrounds
      // odcmAcadLightPalette = palette for light backgrounds (dark lines)
      // odcmAcadDarkPalette = palette for dark backgrounds (light lines)
      const palette = isDarkBackground(color)
        ? this.module.odcmAcadDarkPalette()
        : this.module.odcmAcadLightPalette();
      device.setLogicalPalette(palette as unknown as number, 256);
    }
  }

  /**
   * Get the current background color.
   */
  getBackgroundColor(): number {
    return this.backgroundColor;
  }

  // ==================== Interactive Mode ====================

  /**
   * Check if currently in interactive mode.
   */
  isInInteractiveMode(): boolean {
    const view = this.getActiveView();
    return view ? view.isInInteractivity() : false;
  }

  /**
   * Get the active GsView from the device.
   */
  private getActiveView(): DrawingModule.OdGsView | null {
    const device = this.app.getDevice();
    if (!device) return null;
    return device.viewAt(0);
  }

  // ==================== Navigation ====================

  /**
   * Zoom to fit all entities in view.
   */
  zoomExtents(): void {
    this.app?.ZoomExtents();
    this.update("zoomExtents");
  }

  /**
   * Zoom to a window defined by two world coordinate points.
   * @param minX Lower-left X in world coordinates
   * @param minY Lower-left Y in world coordinates
   * @param maxX Upper-right X in world coordinates
   * @param maxY Upper-right Y in world coordinates
   */
  zoomWindow(minX: number, minY: number, maxX: number, maxY: number): void {
    const gsDevice = this.app.getDevice();
    const gsView = gsDevice.viewAt(0);

    const lowerLeft = new this.module.OdGePoint3d(minX, minY, 0);
    const upperRight = new this.module.OdGePoint3d(maxX, maxY, 0);

    gsView.zoomExtents(lowerLeft, upperRight);

    deleteObj(lowerLeft);
    deleteObj(upperRight);

    this.update("zoomWindow");
    // Un zoom PROGRAMMATIQUE change la vue autant qu'un pincement : sans cet
    // événement, les surcouches écran (cadres d'analyse…) restent projetées
    // sur l'ancienne vue jusqu'au prochain geste de l'utilisateur.
    this.emitViewChanged({ changeType: "zoom" });
  }

  // ==================== Animation ====================

  animate = () => {
    this.animationFrameId = requestAnimationFrame(this.animate);
    this.update("animate");
  };

  /**
   * Force a render update.
   * @param source Caller identifier, attached to Sentry for DR-DRAWING-VA diagnostics.
   */
  update(source: string = "unspecified"): void {
    captureUpdateTelemetry(source);
    this.app.Update();
  }

  /**
   * Throttled update - limits render updates to ~60fps.
   * Use this during drag/interactive operations for better performance.
   * @param source Caller identifier, attached to Sentry for DR-DRAWING-VA diagnostics.
   * @returns true if update was performed, false if throttled
   */
  throttledUpdate(source: string = "unspecified"): boolean {
    const now = performance.now();
    if (now - this.lastThrottledUpdateTime >= this.THROTTLE_INTERVAL_MS) {
      captureUpdateTelemetry(source);
      this.app.Update();
      this.lastThrottledUpdateTime = now;
      return true;
    }
    return false;
  }

  /**
   * Reset throttle timer - call when starting a new interactive operation.
   */
  resetThrottleTimer(): void {
    this.lastThrottledUpdateTime = 0;
  }

  // ==================== Canvas Management ====================

  /**
   * Resize the viewer to match canvas size.
   */
  resize(): void {
    this.canvas.width = this.canvas.clientWidth;
    this.canvas.height = this.canvas.clientHeight;
    this.app.Resize(this.canvas.width, this.canvas.height);
  }

  // ==================== Lineweight ====================

  /** Track current lineweight display state to avoid unnecessary regenAll calls */
  private _lineweightDisplayed: boolean | null = null;

  /** Throttle state for interactive updates */
  private lastThrottledUpdateTime = 0;
  private readonly THROTTLE_INTERVAL_MS = 16; // ~60fps max

  /**
   * Set lineweight display mode WITHOUT regenerating geometry.
   * Use this BEFORE the first render to configure the initial state.
   * The first view.update() will use this setting.
   * @param isDisplayed Whether to display lineweights
   */
  setLineweightDisplayInitial(isDisplayed: boolean): void {
    const db = this.database;
    if (db) {
      LoadingTimer.step(`setLWDISPLAY initial (${isDisplayed})`);
      db.setLWDISPLAY(isDisplayed);
      this._lineweightDisplayed = isDisplayed;
    }
  }

  /**
   * Set lineweight display mode.
   * Only calls regenAll() if the state actually changes.
   * @param isDisplayed Whether to display lineweights
   * @param forceRegen Force regeneration even if state hasn't changed
   */
  displayLineWeight(isDisplayed: boolean, forceRegen = false) {
    const db = this.database;
    if (db) {
      // Skip if state hasn't changed (unless forced)
      if (!forceRegen && this._lineweightDisplayed === isDisplayed) {
        LoadingTimer.step(`displayLineWeight skipped (no change)`);
        return;
      }

      LoadingTimer.step(`setLWDISPLAY(${isDisplayed})`);
      db.setLWDISPLAY(isDisplayed);
      this._lineweightDisplayed = isDisplayed;

      LoadingTimer.step("regenAll start");
      this.app.regenAll();
      noteRegenAll("displayLineWeight");
      LoadingTimer.step("regenAll done");
    }
  }

  /**
   * Reset lineweight state tracking (call when closing file).
   */
  resetLineweightState(): void {
    this._lineweightDisplayed = null;
  }
  /**
   * Regens the view if the flag isRegenNeeded was set to true
   * @returns
   */
  regenIfNeeded(): boolean {
    if (this.isRegenNeeded) {
      this.app.regenAll();
      noteRegenAll("regenIfNeeded");
      this.isRegenNeeded = false;
      return true;
    }
    return false;
  }

  forceRegen(): void {
    this.app.regenAll();
    noteRegenAll("forceRegen");
    this.update("forceRegen");
  }

  // ==================== Lifecycle ====================

  /**
   * Close the current file.
   */
  closeFile(): void {
    // Don't delete the database here — the singleton App holds a raw (non-owning)
    // pointer to it, and deleting it now would leave App with a dangling pointer
    // until the next createNew()/openFile() repoints it. Emscripten does NOT run
    // C++ destructors automatically (unlike a real OdDbDatabasePtr's assignment
    // operator would in C++ — see ODA's own SingleDocDoc.cpp, which relies on that
    // for exactly this "replace the current document" case), so simply dropping
    // the JS reference here leaked the entire database (every entity/layer/block/
    // hatch in it) for the rest of the page's lifetime — this was the cause of
    // DR-DRAWING-XS ("Heap +NNNMb cumulative across drawings"). Defer the actual
    // release to releasePreviousDatabase(), once App has something new to point at.
    if (this.database) {
      this._pendingDatabaseRelease = this.database;
    }
    this.database = null;
    this.resetLineweightState();
  }

  /**
   * Release the database left over from a previous closeFile(), now that App has
   * been repointed at a new one (or is about to open one). Must be called AFTER
   * app.setDb()/App.OpenFile() has already established the new current database —
   * calling it any earlier would free the database App is still using to render.
   */
  private releasePreviousDatabase(): void {
    if (this._pendingDatabaseRelease) {
      deleteObj(this._pendingDatabaseRelease);
      this._pendingDatabaseRelease = null;
      DRDrawingViewer._databasesReleasedCount++;
      Logger.breadcrumb("drawing.database-released", {
        totalReleased: DRDrawingViewer._databasesReleasedCount,
      });
    }
  }

  /**
   * Register a TrueType font with ODA services.
   * This must be called after loading the font to the virtual filesystem
   * so ODA can find it by typeface name.
   * @param fontFileName The font filename (e.g., "arial.ttf")
   */
  registerTTFFont(fontFileName: string): void {
    this.services.ttfFileNameByDescriptor(fontFileName);
  }

  /**
   * Dispose the viewer and release resources.
   */
  dispose(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    this.closeFile();
    // Safe now: this DRDrawingViewer instance is being discarded — nothing will call
    // an App method (Update/getDevice/getDb/...) through it again. The animation
    // frame is already cancelled above, and Facade.dispose() tears down every other
    // manager/dragger/interaction handler before calling this method, so nothing
    // else is left holding a callback into App either. The next drawing gets a
    // brand-new DRDrawingViewer (Facade.initialize() always constructs one), which
    // repoints App via setDb()/OpenFile() before doing anything else with it — so
    // App is never left holding a pointer that anything can actually dereference.
    this.releasePreviousDatabase();

    // Every dragger should already have unsubscribed via its own onDeactivate()
    // (DraggerManager.dispose() runs before this), but clear defensively so a
    // future subscriber that forgets to unsubscribe can't hold a stale
    // reference into a torn-down viewer/dragger graph.
    this.viewChangedHandlers = [];

    // App and Services are page-lifetime singletons owned by DrawingSingleton — not deleted here.
  }
}
